import type { SchriftAtlas, Spielschrift } from "@gui-creator/gemeinsam";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/klient.ts";

/** Lage im Atlas (Pixel), Größe auf dem Bildschirm in GUI-Pixeln und Versatz zur Zeilenoberkante. */
export type Glyph = { bild: HTMLImageElement; sx: number; sy: number; zb: number; zh: number; breite: number; hoehe: number; oben: number; vorschub: number };
export type Glyphen = { zeichen: Map<string, Glyph> };

function ladeBild(src: string): Promise<HTMLImageElement> {
  return new Promise((ok, fehler) => {
    const bild = new Image();
    bild.onload = () => ok(bild);
    bild.onerror = () => fehler(new Error(`Bild ${src} fehlt`));
    bild.src = src;
  });
}

/** Misst jede Glyphe an ihrer rechtesten deckenden Spalte, wie der Spielclient. */
function lies(a: SchriftAtlas, bild: HTMLImageElement, zeichen: Map<string, Glyph>): void {
  const spalten = Math.max(...a.zeilen.map((z) => [...z].length));
  const zb = Math.floor(bild.width / spalten);
  const zh = Math.floor(bild.height / a.zeilen.length);
  if (zb < 1 || zh < 1) return;
  const leinwand = document.createElement("canvas");
  leinwand.width = bild.width;
  leinwand.height = bild.height;
  const ctx = leinwand.getContext("2d", { willReadFrequently: true })!;
  ctx.drawImage(bild, 0, 0);
  const pixel = ctx.getImageData(0, 0, bild.width, bild.height).data;
  const massstab = a.hoehe / zh;
  a.zeilen.forEach((zeile, y) => {
    [...zeile].forEach((z, x) => {
      if (z === "\u0000" || zeichen.has(z)) return;
      const sx = x * zb;
      const sy = y * zh;
      let deckend = 0;
      for (let px = zb - 1; px >= 0 && deckend === 0; px--) {
        for (let py = 0; py < zh; py++) {
          if (pixel[((sy + py) * bild.width + sx + px) * 4 + 3]! > 0) {
            deckend = px + 1;
            break;
          }
        }
      }
      const vorschub = z === " " ? 4 : Math.floor(0.5 + deckend * massstab) + 1;
      zeichen.set(z, { bild, sx, sy, zb, zh, breite: zb * massstab, hoehe: a.hoehe, oben: 7 - a.aufstieg, vorschub });
    });
  });
}

async function baueGlyphen(s: Spielschrift): Promise<Glyphen> {
  const bilder = await Promise.all(s.atlanten.map((a) => ladeBild(`/api/texturen/datei/${a.atlas}`).catch(() => null)));
  const zeichen = new Map<string, Glyph>();
  s.atlanten.forEach((a, i) => {
    const bild = bilder[i];
    if (bild) lies(a, bild, zeichen);
  });
  return { zeichen };
}

export function useSpielschrift(): Glyphen | null {
  const abfrage = useQuery({
    queryKey: ["spielschrift"],
    queryFn: async () => {
      const s = await api<Spielschrift | null>("/api/texturen/schrift");
      return s ? baueGlyphen(s) : null;
    },
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
  return abfrage.data ?? null;
}
